import React, { useState, useEffect } from "react";
import "./StaffDetails.css";
import { useNavigate, useParams } from "react-router-dom";
import { getStaffMemberById, updateStaffMember, toggleStaffStatus, verifyStaffMember } from "../../services/staffManagement";
import LoadingSpinner from "../common/LoadingSpinner/LoadingSpinner";

const StaffDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStaffData();
  }, [id]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getStaffMemberById(id);
      setStaffData(response.data || response);
    } catch (error) {
      console.error("Error fetching staff data:", error);
      setError("Failed to fetch staff data. Please check your connection and try again.");
      setStaffData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateStaffMember(id, staffData);
      alert("Staff member updated successfully!");
      navigate("/staff-managements");
    } catch (error) {
      console.error("Error updating staff:", error);
      setError("Failed to update staff. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleStaffStatus(id, !staffData.isBlocked);
      setStaffData({ ...staffData, isBlocked: !staffData.isBlocked });
      alert(`Staff member ${!staffData.isBlocked ? 'blocked' : 'unblocked'} successfully!`);
    } catch (error) {
      console.error("Error toggling status:", error);
      setError("Failed to update status. Please try again.");
    }
  };

  const handleVerify = async () => {
    try {
      await verifyStaffMember(id);
      setStaffData({ ...staffData, verified: true });
      alert("Staff member verified successfully!");
    } catch (error) {
      console.error("Error verifying staff:", error);
      setError("Failed to verify staff. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="staff-details-container">
        <LoadingSpinner message="Loading staff details..." />
      </div>
    );
  }

  if (!staffData) {
    return (
      <div className="staff-details-container">
        <div className="error-message">
          Staff member not found
          <button onClick={() => navigate("/staff-managements")} className="back-button">
            Back to Staff Management
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-details-container">
      <div className="staff-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          Back
        </button>
        <h1>Staff Information</h1>
        <div className="header-actions">
          {!staffData.verified && (
            <button className="verify-button" onClick={handleVerify}>
              Verify
            </button>
          )}
          <button
            className={`status-button ${staffData.isBlocked ? 'unblock' : 'block'}`}
            onClick={handleToggleStatus}
          >
            {staffData.isBlocked ? 'Unblock' : 'Block'}
          </button>
          <button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
            This might be due to backend connectivity issues. Please ensure the backend server is running.
          </p>
          <button onClick={fetchStaffData} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="details-section">
        <h2>Identity</h2>
        <div className="input-row">
          <div className="input-group">
            <label>First Name:</label>
            <div className="input-value">{staffData.firstName}</div>
          </div>
          <div className="input-group">
            <label>Last Name:</label>
            <div className="input-value">{staffData.lastName}</div>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Contact</h2>
        <div className="input-row">
          <div className="input-group">
            <label>Phone Number:</label>
            <div className="input-value">{staffData.phoneNumber}</div>
          </div>
          <div className="input-group">
            <label>Email:</label>
            <div className="input-value">{staffData.email}</div>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Role & Status</h2>
        <div className="input-row">
          <div className="input-group">
            <label>Position:</label>
            <div className="input-value">{staffData.role}</div>
          </div>
          <div className="input-group">
            <label>Verification Status:</label>
            <div className="input-value">
              <span
                className={`verification-badge ${staffData.verified ? "verified" : "not-verified"
                  }`}
              >
                {staffData.verified ? "Verified" : "Not Verified"}
              </span>
            </div>
          </div>
        </div>
        <div className="input-row">
          <div className="input-group">
            <label>Account Status:</label>
            <div className="input-value">
              <span
                className={`status-badge ${staffData.isBlocked ? "blocked" : "active"
                  }`}
              >
                {staffData.isBlocked ? "Blocked" : "Active"}
              </span>
            </div>
          </div>
          <div className="input-group">
            <label>Member Since:</label>
            <div className="input-value">
              {staffData.createdAt ? new Date(staffData.createdAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDetails;
